import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { GitForkIcon, PlusIcon, SearchIcon, UploadIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import {
  ApiError,
  deleteSkill,
  disableSkill,
  enableSkill,
  FORKS_LOCKED_MESSAGE,
  forkSkill,
  muteSkill,
  pinSkill,
  shareSkillToWorkspace,
  unmuteSkill,
  unpinSkill,
  type Skill,
} from '@/lib/api/skills.js';
import type { SkillsGuideSection } from '@/lib/guide-links.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { forkLockCaption } from './fork-lock.js';
import { OneRowChipFold } from './OneRowChipFold.js';
import { openSkillCreatorChat } from './openSkillCreatorChat.js';
import { SkillCard } from './SkillCard.js';
import { SkillDetail } from './SkillDetail.js';
import type { CopyTarget } from './useSkillCopyTargets.js';
import { isSkillCreatorRow, SKILL_CREATOR_ID } from './skill-creator.js';
import { SkillEditorDialog } from './SkillEditorDialog.js';
import { SuggestedSkillsSection } from './SuggestedSkillsSection.js';
import type { SkillSuggestion } from '@/lib/api/skill-suggestions.js';
import { useSelectedWorkspaceSkillsQuery } from './useSelectedWorkspaceSkills.js';
import { useWorkspaceSkillAccess } from './useWorkspaceSkillAccess.js';

interface SkillsPaneProps {
  /** Shared search text lifted by the directory modal (same box that filters connectors). */
  query: string;
  onQueryChange: (value: string) => void;
  /** Invoked when the user clicks Use in the detail view. Task 18 wires this to the composer. */
  onUseSkill: (skill: Skill) => void;
  /**
   * The workspace whose skills the Workspace tab shows. Omitted in chat, where it follows the
   * switcher; passed explicitly by a surface that targets a specific workspace (the manage page's
   * Agent tab), mirroring `ConnectorsTab`.
   */
  workspaceId?: string | null;
  /** Which scope tab opens first. Defaults to Personal — the manage page opens on Workspace instead. */
  initialScope?: SkillScope;
  /**
   * Which skill's card opens first, if any. Set when the pane is opened to show one specific skill —
   * today, the one a user just created by accepting a suggestion in chat, which is the only creation
   * path that ends outside this pane. Read once at mount, like `initialScope`.
   */
  initialSkillId?: string | null;
  /**
   * Reports which part of the skills guide explains what the pane is currently showing, so the
   * host modal's question mark leads to that section rather than to the top of the article. The
   * pane owns the state the answer depends on — the scope tab, and whether one skill is open.
   */
  onGuideSectionChange?: (section: SkillsGuideSection) => void;
  /** Called when an action leaves the pane for a chat, so the host modal closes itself. */
  onLeaveForChat?: () => void;
}

/**
 * The caller's own private skills, keyed by trigger — the lookup behind "does a twin of this
 * published skill already exist?". A private trigger is unique per user, so forking while its twin
 * exists can only 409; the pane offers "Open my copy" instead of Fork in that state.
 *
 * Built once per list rather than scanned per card: every published card asks the question, so a
 * linear scan makes rendering the grid quadratic in the number of skills.
 */
function indexOwnCopiesByTrigger(skills: Skill[]): Map<string, Skill> {
  const byTrigger = new Map<string, Skill>();
  for (const skill of skills) {
    if (skill.scope === 'private' && !byTrigger.has(skill.trigger)) {
      byTrigger.set(skill.trigger, skill);
    }
  }
  return byTrigger;
}

/** Client-side match over the fields the user actually searches by. */
function matchesQuery(skill: Skill, needle: string): boolean {
  if (!needle) return true;
  const haystack =
    `${skill.trigger} ${skill.title} ${skill.description ?? ''} ${(skill.tags ?? []).join(' ')}`.toLowerCase();
  return haystack.includes(needle);
}

/** The primary views of the list — a skill belongs to exactly one of them: the caller, their workspace, or the org. */
type SkillScope = 'private' | 'workspace' | 'corporate';

const SKILL_SCOPES: readonly string[] = ['private', 'workspace', 'corporate'];

/** `Skill.scope` arrives as a plain string; this narrows it so an unknown value from the server never becomes a tab. */
function isSkillScope(value: string): value is SkillScope {
  return SKILL_SCOPES.includes(value);
}

/**
 * Every distinct tag across `skills`, most-used first (ties alphabetized) —
 * the chip row only ever offers tags that actually match something in the
 * active scope, and the fold in `TagFilterRow` keeps the most useful filters
 * in front of it.
 */
function collectTags(skills: Skill[]): string[] {
  const useCounts = new Map<string, number>();
  for (const skill of skills) {
    for (const tag of skill.tags ?? [])
      useCounts.set(tag, (useCounts.get(tag) ?? 0) + 1);
  }
  return [...useCounts.keys()].sort(
    (left, right) =>
      (useCounts.get(right) ?? 0) - (useCounts.get(left) ?? 0) ||
      left.localeCompare(right),
  );
}

/**
 * Skills category pane of the directory modal — list/search/pin/detail/fork/
 * export. `fetchSkills` returns the caller's own private skills, org-wide
 * corporate ones, and the selected workspace's own, so `scope === 'private'`
 * already means "mine" with no separate owner flag on the wire row. A
 * workspace skill is instead governed by the caller's role in that workspace
 * (`useWorkspaceSkillAccess`), which is what gates authoring here.
 */
export function SkillsPane({
  query,
  onQueryChange,
  onUseSkill,
  workspaceId,
  initialScope,
  initialSkillId,
  onGuideSectionChange,
  onLeaveForChat,
}: SkillsPaneProps): JSX.Element {
  const { data, isLoading } = useQuery(
    useSelectedWorkspaceSkillsQuery(workspaceId),
  );
  const navigate = useNavigate();
  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  /** The workspace the pane shows, resolved the same way `useSelectedWorkspaceSkillsQuery` does. */
  const paneWorkspaceId =
    workspaceId === undefined ? selectedWorkspaceId : workspaceId;
  const workspaceAccess = useWorkspaceSkillAccess(workspaceId);
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSkillId ?? null,
  );
  const [pinPendingId, setPinPendingId] = useState<string | null>(null);
  /**
   * Every fork in flight, not just the last one. A single slot re-enabled the first card's button
   * the moment a second was clicked, and the first request's `finally` then cleared the second
   * card's flag — so a card could be clicked twice mid-request. The duplicate could not create two
   * copies (a private trigger is unique per user, so the server 409s), but the 409 path tells the
   * user "open your copy instead" about a copy that is still being made, which is wrong advice.
   */
  const [forkPendingIds, setForkPendingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  function markForkPending(id: string, pending: boolean): void {
    setForkPendingIds((current) => {
      const next = new Set(current);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }
  const [disablePendingId, setDisablePendingId] = useState<string | null>(null);
  const [mutePendingId, setMutePendingId] = useState<string | null>(null);
  /** `null` when the New-skill dialog is closed; otherwise which tab it opens on. */
  const [editorTab, setEditorTab] = useState<'write' | 'import' | null>(null);
  /** The suggestion the editor is prefilled from, so saving marks that offer accepted. */
  const [fromSuggestion, setFromSuggestion] = useState<SkillSuggestion | null>(
    null,
  );
  /** Primary view — a new user lands on their own (initially empty) personal list, not the org catalog. */
  const [scope, setScope] = useState<SkillScope>(initialScope ?? 'private');
  /** Single-select secondary filter over the active scope's tag set; `null` = no tag filter. */
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const skills = data ?? [];
  const selected =
    selectedId !== null
      ? (skills.find((skill) => skill.id === selectedId) ?? null)
      : null;

  /**
   * The Workspace tab only exists for a shared workspace. If the user switches to a personal one
   * while it is open, fall back to Personal so the list can never sit on a tab that has no source.
   */
  const workspaceTabAvailable = workspaceAccess.workspaceId !== null;
  const activeScope: SkillScope =
    scope === 'workspace' && !workspaceTabAvailable ? 'private' : scope;

  /**
   * Which section of the guide answers "what am I looking at?" right now. One skill open is
   * about what a skill holds; otherwise the answer is the tab the reader is on.
   */
  const guideSection: SkillsGuideSection =
    selected !== null
      ? 'files'
      : activeScope === 'corporate'
        ? 'company'
        : activeScope === 'workspace'
          ? 'workspace'
          : 'library';

  useEffect(() => {
    onGuideSectionChange?.(guideSection);
  }, [guideSection, onGuideSectionChange]);

  /**
   * Pinned is the cross-scope quick-access shelf: it draws from the FULL list
   * so a pinned skill stays visible on any tab (the card's Personal/Workspace/
   * Company badge tells them apart). It renders ABOVE the search/scope/tag
   * controls, so those controls govern only the "All skills" list below them
   * and never touch the shelf. It is a duplicate view, not a move — see `scoped`.
   */
  const pinned = useMemo(
    () => skills.filter((skill) => skill.pinned_at !== null),
    [skills],
  );
  /**
   * The active scope's whole list, pinned skills included. Pinning is a shortcut, not a move: a
   * skill lifted onto the shelf still belongs to its scope, and taking it out of the list below
   * made it look deleted to anyone who went looking for it where they left it. The card carries a
   * Pinned badge in this list, so the same skill in two places reads as one skill, not two.
   */
  const scoped = useMemo(
    () => skills.filter((skill) => skill.scope === activeScope),
    [skills, activeScope],
  );
  /** One pass over the list, so no card has to scan it (see `indexOwnCopiesByTrigger`). */
  const ownCopiesByTrigger = useMemo(
    () => indexOwnCopiesByTrigger(skills),
    [skills],
  );
  /** Chips come from the scoped set — the list the tag filter acts on — so a chip can never filter it down to nothing. */
  const availableTags = useMemo(() => collectTags(scoped), [scoped]);

  const rest = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return scoped.filter(
      (skill) =>
        matchesQuery(skill, needle) &&
        (activeTag === null || (skill.tags ?? []).includes(activeTag)),
    );
  }, [scoped, query, activeTag]);

  /** Switching the primary view drops the tag filter — the other scope has its own tag set. */
  function switchScope(nextScope: SkillScope): void {
    setScope(nextScope);
    setActiveTag(null);
  }

  /**
   * A tag clicked in the detail view: back to the list on the skill's own tab, filtered by that
   * tag. The search text stays. `setScope`, not `switchScope`, so the new tag filter is kept.
   */
  function showSkillsTagged(skill: Skill, tag: string): void {
    setSelectedId(null);
    if (isSkillScope(skill.scope)) setScope(skill.scope);
    setActiveTag(tag);
  }

  /** Authoring is allowed on the caller's own list always, and on the workspace list for an owner/editor. */
  const canAuthorHere =
    activeScope === 'private' ||
    (activeScope === 'workspace' && workspaceAccess.canManage);
  /** Set when the New/Import dialog should create into the workspace rather than the caller's private list. */
  const authorWorkspaceId =
    activeScope === 'workspace' ? workspaceAccess.workspaceId : null;

  /**
   * True when this skill is one the caller may edit: their own private skill, or a workspace skill
   * in a workspace they can author in. The server applies the same rule; this only decides which
   * buttons the detail view offers.
   */
  function canManageSkill(skill: Skill): boolean {
    if (skill.scope === 'private') return true;
    return (
      skill.scope === 'workspace' &&
      skill.workspace_id === workspaceAccess.workspaceId &&
      workspaceAccess.canManage
    );
  }

  /**
   * Copies the caller's private skill into the selected workspace. A 409 means the workspace
   * already publishes that trigger — explained here rather than as a bare status, mirroring
   * `handleFork`; every other failure is already toasted by `assertOk` in `skills.ts`.
   */
  async function handleShareToWorkspace(
    skill: Skill,
    keepCopy: boolean,
  ): Promise<void> {
    const workspaceId = workspaceAccess.workspaceId;
    if (workspaceId === null) return;
    try {
      const shared = await shareSkillToWorkspace(
        skill.id,
        workspaceId,
        keepCopy,
      );
      await qc.invalidateQueries({ queryKey: ['skills'] });
      notify.success(
        `/${skill.trigger} is now available to ${workspaceAccess.workspaceName}.`,
      );
      setSelectedId(shared.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        notify.error(
          `${workspaceAccess.workspaceName} already has a skill /${skill.trigger} — rename yours first.`,
        );
      }
    }
  }

  /**
   * Copies a workspace skill into each picked workspace, one request per target so a 409 in one
   * (the target already has that trigger) does not stop the others. The 409 and a failure that never
   * reached the server are explained here; every other HTTP failure is already toasted by `assertOk`
   * in `skills.ts`. The selection stays put:
   * the copies live in other workspaces.
   */
  async function handleCopyToWorkspaces(
    skill: Skill,
    targets: CopyTarget[],
  ): Promise<void> {
    const copiedNames: string[] = [];
    for (const target of targets) {
      try {
        await shareSkillToWorkspace(skill.id, target.id);
        copiedNames.push(target.name);
      } catch (error) {
        if (!(error instanceof ApiError)) {
          notify.error(
            `Could not copy /${skill.trigger} to ${target.name}. Try again.`,
          );
        } else if (error.status === 409) {
          notify.error(
            `${target.name} already has a skill /${skill.trigger} — rename it there first.`,
          );
        }
      }
    }
    if (copiedNames.length === 0) return;
    await qc.invalidateQueries({ queryKey: ['skills'] });
    notify.success(`/${skill.trigger} copied to ${copiedNames.join(', ')}.`);
  }

  /**
   * Toggles pin state. `pinSkill`/`unpinSkill` already toast on a non-ok
   * response via `assertOk` in `skills.ts`, so the empty catch only stops
   * the rejection from becoming an unhandled promise — it must not add a
   * second, more generic toast on top of the one `assertOk` already showed.
   * `finally` still clears the pending flag on either outcome.
   */
  async function togglePin(skill: Skill): Promise<void> {
    setPinPendingId(skill.id);
    try {
      if (skill.pinned_at !== null) {
        await unpinSkill(skill.id);
      } else {
        await pinSkill(skill.id);
      }
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
    } finally {
      setPinPendingId(null);
    }
  }

  /**
   * Forks a corporate skill into a private copy. A 409 means the caller
   * already owns a private skill with this trigger — the loaded list was
   * stale (the Fork button hides once the twin is visible, see
   * `indexOwnCopiesByTrigger`), so explain the conflict and refetch: the refreshed list
   * flips the detail view's Fork button to "Open my copy". `forkSkill`
   * deliberately skips the generic toast for that status; every other
   * failure it already toasts (see `togglePin`'s JSDoc), so the catch stays
   * quiet about them and just leaves the current skill selected.
   */
  async function handleFork(skill: Skill): Promise<void> {
    if (forkPendingIds.has(skill.id)) return;
    markForkPending(skill.id, true);
    try {
      const copy = await forkSkill(skill.id);
      await qc.invalidateQueries({ queryKey: ['skills'] });
      setSelectedId(copy.id);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        notify.error(
          `You already have a private skill /${skill.trigger} — open your copy instead.`,
        );
        await qc.invalidateQueries({ queryKey: ['skills'] });
      } else if (
        error instanceof ApiError &&
        error.message === FORKS_LOCKED_MESSAGE
      ) {
        notify.error(forkLockCaption(skill));
        await qc.invalidateQueries({ queryKey: ['skills'] });
      }
    } finally {
      markForkPending(skill.id, false);
    }
  }

  /**
   * The card-level fork props for one skill, resolved the same way the detail view resolves its
   * buttons: nothing on a skill the caller owns, "open my copy" when a private twin of the trigger
   * already exists (a second fork could only 409), otherwise a fork.
   */
  function forkPropsFor(skill: Skill): {
    onFork: (() => void) | null;
    onOpenOwnCopy: (() => void) | null;
  } {
    if (skill.scope === 'private') return { onFork: null, onOpenOwnCopy: null };
    const ownCopy = ownCopiesByTrigger.get(skill.trigger) ?? null;
    return {
      onFork: ownCopy ? null : () => void handleFork(skill),
      onOpenOwnCopy: ownCopy ? () => setSelectedId(ownCopy.id) : null,
    };
  }

  /**
   * "Improve in chat" for one skill, or null when it is not on offer: on the skill creator itself,
   * on a workspace skill the caller cannot change (a viewer), or when the creator is missing from
   * the caller's list, is still the older skill doctor, or is switched off for them.
   */
  function improveInChatFor(skill: Skill): (() => void) | null {
    if (skill.scope === 'workspace' && !canManageSkill(skill)) return null;
    const creator = skills.find(
      (candidate) => candidate.id === SKILL_CREATOR_ID,
    );
    if (!isSkillCreatorRow(creator) || skill.id === SKILL_CREATOR_ID)
      return null;
    if (creator.disabled_at !== null || creator.muted_at !== null) return null;
    return () => {
      onLeaveForChat?.();
      openSkillCreatorChat({
        skill,
        workspaceId: paneWorkspaceId,
        navigate,
      }).catch(() => notify.error('Could not open a chat. Try again.'));
    };
  }

  /**
   * Deletes the caller's own private skill. `deleteSkill` already toasts on
   * failure (see `togglePin`'s JSDoc), so the empty catch just keeps the
   * detail view open on a skill that was never actually deleted.
   */
  async function handleDelete(skill: Skill): Promise<void> {
    try {
      await deleteSkill(skill.id);
      await qc.invalidateQueries({ queryKey: ['skills'] });
      setSelectedId(null);
    } catch {}
  }

  /**
   * Flips the owner's pause lever on their own private skill. `disableSkill`/
   * `enableSkill` already toast on failure (see `togglePin`'s JSDoc), so the
   * empty catch only swallows the rejection; the refetched row re-renders the
   * Disabled badge and the button label.
   */
  async function handleToggleDisabled(skill: Skill): Promise<void> {
    setDisablePendingId(skill.id);
    try {
      if (skill.disabled_at !== null) {
        await enableSkill(skill.id);
      } else {
        await disableSkill(skill.id);
      }
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
    } finally {
      setDisablePendingId(null);
    }
  }

  /**
   * Flips this user's personal mute on a corporate skill. `muteSkill`/
   * `unmuteSkill` already toast on failure (see `togglePin`'s JSDoc), so the
   * empty catch only swallows the rejection; the refetched row re-renders
   * the Muted badge and the button label.
   */
  async function handleToggleMuted(skill: Skill): Promise<void> {
    setMutePendingId(skill.id);
    try {
      if (skill.muted_at !== null) {
        await unmuteSkill(skill.id);
      } else {
        await muteSkill(skill.id);
      }
      await qc.invalidateQueries({ queryKey: ['skills'] });
    } catch {
    } finally {
      setMutePendingId(null);
    }
  }

  if (selected) {
    const ownCopy =
      selected.scope === 'private'
        ? null
        : (ownCopiesByTrigger.get(selected.trigger) ?? null);
    return (
      <SkillDetail
        skill={selected}
        onBack={() => setSelectedId(null)}
        onUseSkill={onUseSkill}
        onTogglePin={() => void togglePin(selected)}
        pinPending={pinPendingId === selected.id}
        onFork={() => void handleFork(selected)}
        onOpenOwnCopy={ownCopy ? () => setSelectedId(ownCopy.id) : null}
        onToggleDisabled={() => void handleToggleDisabled(selected)}
        disablePending={disablePendingId === selected.id}
        onToggleMuted={() => void handleToggleMuted(selected)}
        mutePending={mutePendingId === selected.id}
        onDelete={() => void handleDelete(selected)}
        canManage={canManageSkill(selected)}
        onShareToWorkspace={
          selected.scope === 'private' && workspaceAccess.canManage
            ? (keepCopy: boolean) =>
                void handleShareToWorkspace(selected, keepCopy)
            : null
        }
        onCopyToWorkspaces={
          selected.scope === 'workspace' && canManageSkill(selected)
            ? (targets: CopyTarget[]) =>
                void handleCopyToWorkspaces(selected, targets)
            : null
        }
        workspaceName={workspaceAccess.workspaceName}
        onTagClick={(tag) => showSkillsTagged(selected, tag)}
        onImproveInChat={improveInChatFor(selected)}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pt-4 pb-5 md:px-6">
        {!isLoading && pinned.length > 0 && (
          <section
            aria-label="Pinned skills"
            className="flex flex-col gap-3 rounded-xl bg-muted/40 p-4"
          >
            <SectionHeading>Pinned</SectionHeading>
            <SkillGrid
              skills={pinned}
              pinPendingId={pinPendingId}
              forkPendingIds={forkPendingIds}
              forkPropsFor={forkPropsFor}
              onOpen={setSelectedId}
              onTogglePin={(skill) => void togglePin(skill)}
              showPinnedBadge={false}
            />
          </section>
        )}

        <section
          aria-label="All skills"
          className="flex flex-col gap-3 rounded-xl bg-muted/40 p-4"
        >
          <SectionHeading>All skills</SectionHeading>
          <div className="relative">
            <SearchIcon
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search skills…"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              aria-label="Search skills"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ScopeTabs
              scope={activeScope}
              onSwitch={switchScope}
              workspaceLabel={
                workspaceTabAvailable ? workspaceAccess.workspaceName : null
              }
            />
            {canAuthorHere && (
              <div className="ml-auto flex gap-2">
                <Button size="sm" onClick={() => setEditorTab('write')}>
                  <PlusIcon
                    className="size-3.5"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  New skill
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditorTab('import')}
                >
                  <UploadIcon
                    className="size-3.5"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  Import
                </Button>
              </div>
            )}
          </div>
          {availableTags.length > 0 && (
            <TagFilterRow
              key={activeScope}
              tags={availableTags}
              activeTag={activeTag}
              onToggle={setActiveTag}
            />
          )}

          {!isLoading && activeScope !== 'private' && rest.length > 0 && (
            <ForkExplainer scope={activeScope} />
          )}

          {isLoading ? (
            <SkillListSkeleton />
          ) : rest.length > 0 ? (
            <SkillGrid
              skills={rest}
              pinPendingId={pinPendingId}
              forkPendingIds={forkPendingIds}
              forkPropsFor={forkPropsFor}
              onOpen={setSelectedId}
              onTogglePin={(skill) => void togglePin(skill)}
              showPinnedBadge
            />
          ) : (
            <EmptyState
              scope={activeScope}
              query={query}
              activeTag={activeTag}
              workspaceName={workspaceAccess.workspaceName}
            />
          )}
        </section>

        {/*
          Last, and always its own section.

          It used to sit at the very top, which put the platform's guesses in front of the user's own
          library every time they opened the pane — that inverts whose page this is, since they came to
          find a skill they wrote, not to review proposals. Below the list it stays findable without
          claiming the first word. It renders nothing at all when there is nothing waiting, so the
          position costs an empty pane nothing.
        */}
        <SuggestedSkillsSection
          onOpenEditor={(suggestion) => {
            setFromSuggestion(suggestion);
            setEditorTab('write');
          }}
        />
      </div>

      {editorTab !== null && (
        <SkillEditorDialog
          open
          onOpenChange={(openValue) => {
            if (!openValue) {
              setEditorTab(null);
              setFromSuggestion(null);
            }
          }}
          initialTab={editorTab}
          workspaceId={authorWorkspaceId}
          initialDraft={fromSuggestion?.draft ?? null}
          suggestionId={fromSuggestion?.id ?? null}
          onSaved={(saved) => {
            setEditorTab(null);
            setFromSuggestion(null);
            setSelectedId(saved.id);
          }}
        />
      )}
    </div>
  );
}

/**
 * Personal | Workspace &lt;name&gt; | Corporate segmented control — the mutually-exclusive primary view
 * of the list. The middle tab reads "Workspace <name>": the bare name alone left people guessing
 * whether the tab meant a workspace or another category, so the word carries the scope and the name
 * says which one. It appears only when `workspaceLabel` is set, i.e. the selected workspace is
 * shared and can hold skills at all.
 */
function ScopeTabs({
  scope,
  onSwitch,
  workspaceLabel,
}: {
  scope: SkillScope;
  onSwitch: (scope: SkillScope) => void;
  workspaceLabel: string | null;
}): JSX.Element {
  const tabClass = (active: boolean) =>
    cn(
      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
      active
        ? 'bg-foreground text-background'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );
  return (
    <div
      role="tablist"
      aria-label="Skill scope"
      className="flex gap-0.5 rounded-full border border-border/60 bg-muted/30 p-0.5"
    >
      <button
        type="button"
        role="tab"
        aria-selected={scope === 'private'}
        className={tabClass(scope === 'private')}
        onClick={() => onSwitch('private')}
      >
        Personal
      </button>
      {workspaceLabel !== null && (
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'workspace'}
          className={cn(tabClass(scope === 'workspace'), 'max-w-48 truncate')}
          onClick={() => onSwitch('workspace')}
        >
          {`Workspace ${workspaceLabel}`}
        </button>
      )}
      <button
        type="button"
        role="tab"
        aria-selected={scope === 'corporate'}
        className={tabClass(scope === 'corporate')}
        onClick={() => onSwitch('corporate')}
      >
        Corporate
      </button>
    </div>
  );
}

/**
 * Single-select tag chips (Jira-labels idiom) over the active scope's tag
 * set — clicking the active tag clears the filter. Collapsed, the row is
 * exactly ONE line: `OneRowChipFold` measures how many chips fit next to the
 * "+N more" toggle at the current pane width (a count-based limit can't
 * promise a single row). `tags` arrives most-used-first, so the visible row
 * keeps the most useful filters, and the active tag is pinned into it — an
 * invisible active filter would look like a broken list. "+N more" expands
 * to the full wrapped list with a "Show less" at the end.
 */
function TagFilterRow({
  tags,
  activeTag,
  onToggle,
}: {
  tags: string[];
  activeTag: string | null;
  onToggle: (tag: string | null) => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const foldChipClass =
    'rounded-full border border-border/60 px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-border hover:text-foreground';
  const tagChip = (tag: string) => {
    const active = tag === activeTag;
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={() => onToggle(active ? null : tag)}
        className={cn(
          'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
          active
            ? 'border-foreground bg-foreground text-background'
            : 'border-border/60 text-muted-foreground hover:border-border hover:text-foreground',
        )}
      >
        {tag}
      </button>
    );
  };
  if (expanded) {
    return (
      <div className="flex flex-wrap gap-1.5" aria-label="Filter by tag">
        {tags.map((tag) => (
          <span key={tag}>{tagChip(tag)}</span>
        ))}
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className={foldChipClass}
        >
          Show less
        </button>
      </div>
    );
  }
  return (
    <OneRowChipFold
      aria-label="Filter by tag"
      className="gap-1.5"
      gapPx={6}
      chips={tags.map((tag) => ({ key: tag, node: tagChip(tag) }))}
      pinnedKey={activeTag}
      renderOverflow={(hiddenCount) => (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={foldChipClass}
        >
          {`+${hiddenCount} more`}
        </button>
      )}
    />
  );
}

/** Per-scope empty copy: a new user's personal list explains what belongs here; the workspace and corporate views say nothing is published yet. A search with no match names the search text and the active tag. */
function EmptyState({
  scope,
  query,
  activeTag,
  workspaceName,
}: {
  scope: SkillScope;
  query: string;
  activeTag: string | null;
  workspaceName: string | null;
}): JSX.Element {
  if (query.trim()) {
    const tagSuffix = activeTag === null ? '' : ` with tag "${activeTag}"`;
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{`No skills match "${query}"${tagSuffix}.`}</p>
    );
  }
  if (scope === 'workspace') {
    return (
      <div className="flex flex-col items-center gap-1.5 py-8 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">No workspace skills yet</p>
        <p className="max-w-md">
          {`Skills here belong to ${workspaceName ?? 'this workspace'} — everyone in it can use them, and the agent offers them in its chats. Owners and editors can create one, or share one of their personal skills.`}
        </p>
      </div>
    );
  }
  if (scope === 'corporate') {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No corporate skills yet — skills your organization publishes will appear
        here.
      </p>
    );
  }
  return (
    <div className="flex flex-col items-center gap-1.5 py-8 text-center text-sm text-muted-foreground">
      <p className="font-medium text-foreground">No personal skills yet</p>
      <p className="max-w-md">
        Your private skills live here. Create one to turn a repeatable task into
        a reusable workflow — or bring your skills from Claude or ChatGPT:
        paste, drop .md files, or a whole .zip or .skill bundle.
      </p>
    </div>
  );
}

/** Uppercase label heading the two list sections — Pinned and All skills. */
function SectionHeading({ children }: { children: string }): JSX.Element {
  return (
    <h3 className="px-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </h3>
  );
}

/**
 * The one line that tells people forking exists at all. It sits above a published list — corporate
 * or workspace — because that is where a skill someone else wrote is in front of them and the
 * thought "I wish this said X instead" occurs. It names the personal copy explicitly: forking is
 * NOT the workspace share, and conflating the two teaches the wrong model of a three-scope system.
 * Rendered only when the list has something in it, so an empty tab does not advertise an action
 * with no target.
 */
function ForkExplainer({
  scope,
}: {
  scope: Exclude<SkillScope, 'private'>;
}): JSX.Element {
  const source = scope === 'corporate' ? 'company' : 'workspace';
  return (
    <p className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-xs text-muted-foreground">
      <GitForkIcon
        className="mr-1.5 inline size-3.5 align-text-bottom"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      {`Need one of these your way? Fork it into a personal copy and edit it however you like — the ${source} version stays as it is.`}
    </p>
  );
}

function SkillGrid({
  skills,
  pinPendingId,
  forkPendingIds,
  forkPropsFor,
  onOpen,
  onTogglePin,
  showPinnedBadge,
}: {
  skills: Skill[];
  pinPendingId: string | null;
  forkPendingIds: ReadonlySet<string>;
  forkPropsFor: (skill: Skill) => {
    onFork: (() => void) | null;
    onOpenOwnCopy: (() => void) | null;
  };
  onOpen: (id: string) => void;
  onTogglePin: (skill: Skill) => void;
  showPinnedBadge: boolean;
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {skills.map((skill) => (
        <SkillCard
          key={skill.id}
          skill={skill}
          onOpen={() => onOpen(skill.id)}
          onTogglePin={() => onTogglePin(skill)}
          pinPending={pinPendingId === skill.id}
          {...forkPropsFor(skill)}
          forkPending={forkPendingIds.has(skill.id)}
          showPinnedBadge={showPinnedBadge}
        />
      ))}
    </div>
  );
}

function SkillListSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-4"
        >
          <Skeleton className="h-3 w-2/3" />
          <Skeleton className="h-2.5 w-1/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}
